// WORLD COSTA BLANCA — visual runtime entry.
//
// 2026-08-17 (Kimi K3): unified runtime. The visual app now runs ON the
// tested semantic spine (prototype/src/engine) instead of parallel ad-hoc
// state: WorldStore (canonical records) + WorldState (semantic state, no
// camera) + CameraAuthority (one camera writer per frame) + EventBus.
// Return contract: opening a detail stores {authority, pose}; closing
// restores exactly (MASTER PROMPT §7, doc 19 world-state continuity).

import * as THREE from 'three';
import semanticWorld from '../../prototype/worlds/costa-blanca-v0.world.json';
import { WorldStore } from '../../prototype/src/engine/world-store.js';
import { WorldState } from '../../prototype/src/engine/world-state.js';
import { CameraAuthority } from '../../prototype/src/engine/camera-authority.js';
import { EventBus, EVENTS } from '../../prototype/src/engine/event-bus.js';
import { CAMERA_AUTHORITY, EXPERIENCE_MODE } from '../../prototype/src/engine/types.js';
import { buildAlteaSlice } from './scene-builders.js';
import { buildSky, buildRidgeLines, SKY } from './core/sky.js';
import { Walker } from './runtime/walker.js';
import { OrbitController, WalkController, HeldPoseController } from './runtime/controllers.js';
import { qaParams, markFrameRendered } from './runtime/qa.js';

const QA = qaParams();
const REDUCED = QA.reducedMotion || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || QA.enabled;

// ---------- renderer / scene ----------
const canvas = document.querySelector('#world');
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.8));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.12;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;

const scene = new THREE.Scene();
scene.background = new THREE.Color(SKY.haze); // fallback behind the painted dome
scene.fog = new THREE.FogExp2(0xf2e4d2, .0075); // warm coastal haze
buildSky(scene);
buildRidgeLines(scene);

const camera = new THREE.PerspectiveCamera(42, window.innerWidth / window.innerHeight, .1, 420);

addLighting(scene);

// ---------- semantic engine ----------
const bus = new EventBus({ recordHistory: true });
const store = new WorldStore(semanticWorld);
const state = new WorldState({ store, bus });
state.assertNoCameraState();

// ---------- world geometry + collision ----------
const { root, interactables, anchors, collision } = buildAlteaSlice(scene, semanticWorld, {});
root.rotation.y = .03;

// ---------- camera authority + controllers ----------
const authority = new CameraAuthority({
  bus,
  pose: { position: [32, 27, 34], target: [1, 2, 4], fov: 42 },
  reducedMotion: REDUCED
});

const overview = new OrbitController(renderer.domElement);
const walker = new Walker(renderer.domElement, collision, { x: -8, z: 10.0, yaw: -1.22, pitch: 0.0 });
const walk = new WalkController(walker);
const focus = new HeldPoseController('FOCUS');
const mapView = new HeldPoseController('MAP');
mapView.setPose({ position: [2, 52, 16], target: [2, 0, 4], fov: 42 });
const directed = new HeldPoseController('DIRECTED');

authority.register(CAMERA_AUTHORITY.OVERVIEW, overview);
authority.register(CAMERA_AUTHORITY.WALK, walk);
authority.register(CAMERA_AUTHORITY.FOCUS, focus);
authority.register(CAMERA_AUTHORITY.MAP, mapView);
authority.register(CAMERA_AUTHORITY.DIRECTED, directed);
authority.request(CAMERA_AUTHORITY.OVERVIEW, { reason: 'boot' });

// ---------- interaction ----------
const entityById = new Map(semanticWorld.entities.map((entity) => [entity.id, entity]));
const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();
let hoveredRoot = null;
const hotspotLabel = createHotspotLabel();

/** Return contract: exact authority + pose to restore after deep content. */
let returnContext = null;
let guidedTimer = null;

const ui = {
  intro: document.querySelector('#intro'),
  enter: document.querySelector('#enter-world'),
  openSearch: document.querySelector('#open-search'),
  detail: document.querySelector('#detail'),
  detailClose: document.querySelector('#detail-close'),
  detailEyebrow: document.querySelector('#detail-eyebrow'),
  detailTitle: document.querySelector('#detail-title'),
  detailSummary: document.querySelector('#detail-summary'),
  detailFacts: document.querySelector('#detail-facts'),
  detailSarah: document.querySelector('#detail-sarah'),
  detailDisclosure: document.querySelector('#detail-disclosure'),
  save: document.querySelector('#save-property'),
  requestVisit: document.querySelector('#request-visit'),
  search: document.querySelector('#search-panel'),
  searchClose: document.querySelector('#search-close'),
  price: document.querySelector('#filter-price'),
  priceOutput: document.querySelector('#price-output'),
  beds: document.querySelector('#filter-beds'),
  airport: document.querySelector('#filter-airport'),
  results: document.querySelector('#search-results'),
  modeButtons: [...document.querySelectorAll('[data-mode]')],
  hint: document.querySelector('#world-hint'),
  crosshair: document.querySelector('#crosshair')
};

const HINTS = {
  EXPLORE: 'Drag to orbit · Scroll to zoom · Click a highlighted object',
  WALK: 'Click the world to grab the mouse · WASD to walk · Shift to run · E to open · Esc to release',
  MAP: 'Top-down orientation · Click an entity to focus it · Explore to return',
  SEARCH: 'Filter without walking: the world dims to what matches'
};

ui.enter.addEventListener('click', () => ui.intro.classList.add('hidden'));
ui.openSearch.addEventListener('click', () => { ui.intro.classList.add('hidden'); setMode('SEARCH'); openSearch(); });
ui.detailClose.addEventListener('click', closeDetail);
ui.searchClose.addEventListener('click', () => ui.search.classList.remove('open'));
ui.save.addEventListener('click', () => state.focusedEntityId && toggleSave(state.focusedEntityId));
ui.requestVisit.addEventListener('click', () => state.focusedEntityId && state.requestVisit(state.focusedEntityId));
ui.modeButtons.forEach((button) => button.addEventListener('click', () => setMode(button.dataset.mode)));

for (const input of [ui.price, ui.beds, ui.airport]) input.addEventListener('input', updateSearch);

renderer.domElement.addEventListener('pointermove', onPointerMove);
renderer.domElement.addEventListener('pointerdown', onPointerDown);
window.addEventListener('resize', onResize);

walker.onInteract = (entityId) => { if (entityId) openEntity(entityId); };

// Restore semantic state (saved / visit-requested) from a previous session.
restorePersistedState();
bus.on(EVENTS.WORLD_STATE_CHANGED, persistState);
bus.on(EVENTS.PROPERTY_SAVED, refreshActionLabels);
bus.on(EVENTS.VISIT_REQUESTED, refreshActionLabels);

applyQaShotFromUrl();
updateSearch();
requestAnimationFrame(animate);

// ---------- modes ----------
function markModeUi(mode) {
  ui.modeButtons.forEach((button) => button.classList.toggle('active', button.dataset.mode === mode));
  ui.hint.textContent = HINTS[mode] || HINTS.EXPLORE;
  ui.crosshair?.classList.toggle('visible', mode === 'WALK');
}

function setMode(mode) {
  stopGuided();
  markModeUi(mode);

  if (mode === 'WALK') {
    ui.search.classList.remove('open');
    state.setMode(EXPERIENCE_MODE.EXPLORE);
    authority.request(CAMERA_AUTHORITY.WALK, { reason: 'mode-walk', durationMs: 900, endPose: walker.pose() });
    if (!QA.enabled) walker.lock();
    return;
  }
  if (mode === 'MAP') {
    ui.search.classList.remove('open');
    authority.request(CAMERA_AUTHORITY.MAP, { reason: 'mode-map', durationMs: 900, endPose: mapView.pose });
    return;
  }
  if (mode === 'GUIDED') {
    ui.search.classList.remove('open');
    state.setMode(EXPERIENCE_MODE.GUIDED);
    startGuidedRoute();
    return;
  }
  if (mode === 'SEARCH') {
    state.setMode(EXPERIENCE_MODE.SEARCH);
    openSearch();
    return;
  }
  // EXPLORE
  state.setMode(EXPERIENCE_MODE.EXPLORE);
  ui.search.classList.remove('open');
  authority.request(CAMERA_AUTHORITY.OVERVIEW, { reason: 'mode-explore', durationMs: 900, endPose: { position: [32, 27, 34], target: [1, 2, 4], fov: 42 } });
}

// ---------- guided route v1 (deterministic, data-anchored) ----------
function startGuidedRoute() {
  const stops = [
    { entityId: 'entity.sarah.world-guide', ms: 2600 },
    { entityId: 'property.family-02', ms: 3400 },
    { entityId: 'property.sea-01', ms: 3400 },
    { entityId: 'property.hills-03', ms: 3400 }
  ];
  let index = -1;
  const next = () => {
    index += 1;
    if (index >= stops.length) {
      guidedTimer = null;
      setMode('EXPLORE');
      return;
    }
    const stop = stops[index];
    const pose = approachPose(stop.entityId);
    if (pose) {
      directed.setPose(pose);
      authority.request(CAMERA_AUTHORITY.DIRECTED, { reason: `guided-stop-${index}`, durationMs: REDUCED ? 0 : 1400, endPose: pose });
    }
    openEntity(stop.entityId, { keepCamera: true });
    guidedTimer = setTimeout(next, REDUCED ? 400 : stop.ms);
  };
  next();
}

function stopGuided() {
  if (guidedTimer) {
    clearTimeout(guidedTimer);
    guidedTimer = null;
  }
}

// ---------- detail / return contract ----------
function openEntity(entityId, { keepCamera = false } = {}) {
  const entity = entityById.get(entityId);
  if (!entity) return;
  state.focus(entityId);
  ui.intro.classList.add('hidden');
  ui.search.classList.remove('open');
  ui.detail.classList.add('open');
  ui.detailEyebrow.textContent = entity.kind === 'PROPERTY' ? 'PROPERTY / DEMO' : entity.kind.replaceAll('_', ' ');
  ui.detailTitle.textContent = entity.content?.title || entity.id;
  ui.detailSummary.textContent = entity.content?.summary || '';
  ui.detailFacts.innerHTML = entity.kind === 'PROPERTY' ? propertyFacts(entity) : '';
  ui.detailSarah.textContent = entity.content?.sarahCommentary
    || 'Use the world as orientation, then cross into verified evidence before making a buyer decision.';
  ui.detailDisclosure.textContent = disclosure(entity);
  ui.save.style.display = entity.kind === 'PROPERTY' ? '' : 'none';
  ui.requestVisit.style.display = entity.kind === 'PROPERTY' ? '' : 'none';
  refreshActionLabels();

  if (!keepCamera) {
    returnContext = { authority: authority.owner, pose: structuredClone(authority.pose) };
    const pose = approachPose(entityId);
    if (pose) {
      focus.setPose(pose);
      authority.request(CAMERA_AUTHORITY.FOCUS, { reason: `open-${entityId}`, durationMs: REDUCED ? 0 : 850, endPose: pose });
    }
  }
}

function closeDetail() {
  ui.detail.classList.remove('open');
  stopGuided();
  state.focus(null);
  if (returnContext) {
    const target = returnContext.authority === CAMERA_AUTHORITY.TRANSITION ? CAMERA_AUTHORITY.OVERVIEW : returnContext.authority;
    const owner = authority._controllers.has(target) ? target : CAMERA_AUTHORITY.OVERVIEW;
    authority.request(owner, { reason: 'return-contract', durationMs: REDUCED ? 0 : 700, endPose: returnContext.pose });
    returnContext = null;
  }
}

function approachPose(entityId) {
  const anchor = anchors.get(entityId);
  if (!anchor) return null;
  // Per-entity framing wins over the global offset (mission: no blind shared
  // offset when it produces bad framing/occlusion — INC-005 lesson).
  const entity = entityById.get(entityId);
  const offset = entity?.approach?.offset || [5.4, 4.4, 6.8];
  const lift = entity?.approach?.targetLift ?? 0.65;
  return {
    position: [anchor.x + offset[0], anchor.y + offset[1], anchor.z + offset[2]],
    target: [anchor.x, anchor.y + lift, anchor.z],
    fov: 42
  };
}

// ---------- search / filters ----------
function openSearch() {
  ui.detail.classList.remove('open');
  ui.search.classList.add('open');
  updateSearch();
}

function updateSearch() {
  const filters = {
    maxPrice: Number(ui.price.value),
    minBedrooms: Number(ui.beds.value),
    maxAirportMinutes: Number(ui.airport.value)
  };
  state.setFilters(filters);
  ui.priceOutput.textContent = euroShort(filters.maxPrice);

  const matches = semanticWorld.entities.filter((entity) => {
    if (entity.kind !== 'PROPERTY') return false;
    const p = entity.property;
    return p.price <= filters.maxPrice && p.bedrooms >= filters.minBedrooms && p.airportMinutes <= filters.maxAirportMinutes;
  });

  ui.results.innerHTML = matches.length ? matches.map((entity) => `
    <button class="result" data-entity="${entity.id}">
      <strong>${entity.content.title}</strong>
      <span>${euro(entity.property.price)} · ${entity.property.bedrooms} beds · airport ${entity.property.airportMinutes} min</span>
    </button>`).join('') : '<p>No demo property survives these filters. That is a valid result: do not fill the shortlist with weak matches.</p>';

  ui.results.querySelectorAll('[data-entity]').forEach((button) => button.addEventListener('click', () => openEntity(button.dataset.entity)));
  applyWorldFilter(matches.map((entity) => entity.id));
}

const FILTER_GREY = new THREE.Color(0xa9a49a); // warm stone grey, not ghost

function applyWorldFilter(matchedIds) {
  const set = new Set(matchedIds);
  const searching = state.mode === EXPERIENCE_MODE.SEARCH && ui.search.classList.contains('open');
  for (const group of interactables) {
    const id = group.userData.entityId;
    const entity = entityById.get(id);
    if (entity?.kind !== 'PROPERTY') continue;
    const match = !searching || set.has(id);
    group.traverse((object) => {
      const m = object.material;
      if (!m || !m.color) return;
      // Non-destructive de-emphasis (mission: no 18%-opacity ghost buildings).
      // Non-matches stay opaque; they desaturate toward warm grey and their
      // beacon goes quiet. Matches restore their authored color.
      if (!m.userData.baseColor) {
        m.userData.baseColor = m.color.clone();
        m.userData.baseEmissiveIntensity = m.emissiveIntensity ?? 0;
      }
      if (match) {
        m.color.copy(m.userData.baseColor);
        if (m.emissive) m.emissiveIntensity = m.userData.baseEmissiveIntensity;
      } else {
        m.color.copy(m.userData.baseColor).lerp(FILTER_GREY, .62);
        if (m.emissive) m.emissiveIntensity = .08;
      }
    });
  }
}

// ---------- save / visit ----------
function toggleSave(entityId) {
  if (state.savedPropertyIds.has(entityId)) {
    // engine has no unsave; rejection would be wrong, so toggle locally
    state.savedPropertyIds.delete(entityId);
    state.setPropertyState(entityId, 'OPENED');
    refreshActionLabels();
    persistState();
  } else {
    state.saveProperty(entityId);
  }
}

function refreshActionLabels() {
  const id = state.focusedEntityId;
  if (!id) return;
  ui.save.textContent = state.savedPropertyIds.has(id) ? 'Saved ✓' : 'Save';
  ui.requestVisit.textContent = state.visitRequestedPropertyIds.has(id) ? 'Visit requested ✓' : 'Request visit';
}

// ---------- persistence adapter (single authority: WorldState) ----------
const STORAGE_KEY = 'wcb.world-state.v1';

function persistState() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state.snapshot()));
  } catch { /* storage may be unavailable; world stays navigable */ }
}

function restorePersistedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const snap = JSON.parse(raw);
    for (const id of snap.savedPropertyIds || []) if (entityById.has(id)) state.saveProperty(id);
    for (const id of snap.visitRequestedPropertyIds || []) if (entityById.has(id)) state.requestVisit(id);
  } catch { /* corrupted state degrades to a fresh world */ }
}

// ---------- pointer interaction ----------
function onPointerMove(event) {
  if (authority.owner === CAMERA_AUTHORITY.WALK && walker.locked) return;
  pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
  pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(pointer, camera);
  const hits = raycaster.intersectObjects(interactables, true);
  const rootHit = hits.length ? findEntityRoot(hits[0].object) : null;
  if (hoveredRoot !== rootHit) {
    setHovered(hoveredRoot, false);
    hoveredRoot = rootHit;
    setHovered(hoveredRoot, true);
  }
  renderer.domElement.style.cursor = hoveredRoot ? 'pointer' : 'grab';
  if (hoveredRoot) {
    const entity = entityById.get(hoveredRoot.userData.entityId);
    hotspotLabel.textContent = entity?.content?.title || hoveredRoot.userData.entityId;
    hotspotLabel.style.left = `${event.clientX}px`;
    hotspotLabel.style.top = `${event.clientY - 12}px`;
    hotspotLabel.classList.add('visible');
  } else hotspotLabel.classList.remove('visible');
}

function onPointerDown(event) {
  if (event.button !== 0) return;
  if (authority.owner === CAMERA_AUTHORITY.WALK) {
    if (!walker.locked) walker.lock();
    return;
  }
  pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
  pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(pointer, camera);
  const hit = raycaster.intersectObjects(interactables, true)[0];
  const rootHit = hit ? findEntityRoot(hit.object) : null;
  if (rootHit?.userData?.entityId) openEntity(rootHit.userData.entityId);
}

// In walk mode the walker picks from screen centre (crosshair).
function updateWalkPick() {
  if (authority.owner !== CAMERA_AUTHORITY.WALK || !walker.locked) {
    if (walker.hovered) { walker.hovered = null; hotspotLabel.classList.remove('visible'); }
    return;
  }
  raycaster.setFromCamera({ x: 0, y: 0 }, camera);
  const hits = raycaster.intersectObjects(interactables, true);
  const rootHit = hits.length ? findEntityRoot(hits[0].object) : null;
  walker.hovered = rootHit?.userData?.entityId || null;
  if (walker.hovered) {
    const entity = entityById.get(walker.hovered);
    hotspotLabel.textContent = `${entity?.content?.title || walker.hovered} — E`;
    hotspotLabel.style.left = '50%';
    hotspotLabel.style.top = '56%';
    hotspotLabel.classList.add('visible');
  } else hotspotLabel.classList.remove('visible');
}

function findEntityRoot(object) {
  let current = object;
  while (current && current !== scene) {
    if (current.userData?.entityId) return current;
    current = current.parent;
  }
  return null;
}

function setHovered(group, hovered) {
  if (!group) return;
  group.scale.setScalar(hovered ? 1.16 : 1);
}

// ---------- QA ----------
function applyQaShotFromUrl() {
  if (!QA.enabled) return;
  ui.intro.classList.add('hidden');
  const views = {
    overview: { position: [32, 27, 34], target: [1, 2, 4], fov: 42 },
    lane: { position: [-4, 5.8, 12.5], target: [-1.5, 2.2, 0], fov: 42 },
    mirador: { position: [10.5, 8.3, 10], target: [5.5, 3.5, -3.5], fov: 42 },
    coast: { position: [-22, 16, 31], target: [-2, 1.4, 5], fov: 42 },
    properties: { position: [17, 15, 13], target: [4, 3, -1], fov: 42 },
    map: null, // MAP controller pose
    'walk-spawn': null // walker spawn pose
  };
  const name = QA.shot || QA.legacyView;
  if (name === 'map') {
    markModeUi('MAP');
    authority.request(CAMERA_AUTHORITY.MAP, { reason: 'qa', durationMs: 0, endPose: mapView.pose });
    return;
  }
  if (name === 'walk-spawn') {
    markModeUi('WALK');
    walker.setPose({ x: -8, z: 10.0, yaw: -1.22, pitch: 0.0 });
    authority.request(CAMERA_AUTHORITY.WALK, { reason: 'qa', durationMs: 0, endPose: walker.pose() });
    return;
  }
  if (views[name]) {
    markModeUi('EXPLORE');
    overview.onGain(views[name]);
    authority.request(CAMERA_AUTHORITY.OVERVIEW, { reason: 'qa', durationMs: 0, endPose: views[name] });
  }
}

// ---------- frame loop ----------
const FRAME_TIMES_MAX = 4096;
const frameTimesRing = [];
let lastTime = 0;
let lastFrameStart = 0;
function animate(time = 0) {
  if (lastFrameStart && frameTimesRing.length < FRAME_TIMES_MAX) {
    frameTimesRing.push(time - lastFrameStart);
  }
  lastFrameStart = time;
  const dt = Math.min((time - lastTime) / 1000 || 0.016, 0.1);
  lastTime = time;

  const pose = authority.update(dt);
  camera.position.set(pose.position[0], pose.position[1], pose.position[2]);
  if (Math.abs(camera.fov - pose.fov) > 1e-3) {
    camera.fov = pose.fov;
    camera.updateProjectionMatrix();
  }
  camera.lookAt(pose.target[0], pose.target[1], pose.target[2]);

  if (!QA.freeze) {
    const t = time * .001;
    scene.traverse((object) => {
      if (object.userData.entityKind === 'PROPERTY') {
        const beacon = object.children.find((child) => child.userData?.beacon || child.geometry?.type === 'SphereGeometry');
        if (beacon) {
          if (beacon.userData.baseY === undefined) beacon.userData.baseY = beacon.position.y;
          beacon.position.y = beacon.userData.baseY + Math.sin(t * 1.25 + object.id) * .025;
        }
      }
      // ambient world life (deterministic under QA freeze)
      if (object.userData.ambient === 'gull') {
        object.position.y = object.userData.baseY + Math.sin(t * .9 + object.userData.phase) * .4;
        object.rotation.z = Math.sin(t * .7 + object.userData.phase) * .14;
      } else if (object.userData.ambient === 'plane') {
        object.position.x = -130 + ((t * 4 + 40) % 260); // ~65s sky crossing
      }
    });
  }

  updateWalkPick();
  renderer.render(scene, camera);
  markFrameRendered();
  requestAnimationFrame(animate);
}

// ---------- helpers ----------
function addLighting(target) {
  // Luminist Mediterranean setup (docs/04 + mission brief): golden-late sun,
  // luminous whites, and blue/mauve colored shadows — no dead blacks.
  const hemi = new THREE.HemisphereLight(0xc6dbec, 0x9c8a74, 1.85);
  target.add(hemi);

  const sun = new THREE.DirectionalLight(0xffdfae, 3.3);
  sun.position.set(-16, 18, 20);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.left = -35;
  sun.shadow.camera.right = 35;
  sun.shadow.camera.top = 35;
  sun.shadow.camera.bottom = -35;
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 80;
  sun.shadow.bias = -0.0004;
  target.add(sun);

  // cool blue-violet bounce from the shadow side — colored shadows
  const bounce = new THREE.DirectionalLight(0xa4b4e2, .55);
  bounce.position.set(18, 8, 30);
  target.add(bounce);

  // faint mauve ambient so shade never reads as dead black
  target.add(new THREE.AmbientLight(0x7d7090, .3));
}

function propertyFacts(entity) {
  const p = entity.property;
  const facts = [
    ['Price', euro(p.price)],
    ['Bedrooms', String(p.bedrooms)],
    ['Sea', `${p.seaMinutes} min`],
    ['Airport', `${p.airportMinutes} min`],
    ['Type', p.type],
    ['Status', p.newBuild ? 'New build' : 'Resale']
  ];
  return facts.map(([label, value]) => `<div class="fact"><small>${label}</small><strong>${value}</strong></div>`).join('');
}

function disclosure(entity) {
  if (entity.fictionStatus === 'ILLUSTRATIVE_COMPOSITE') return 'Illustrative composite used for prototype validation. Not a live property listing or real testimonial.';
  if (entity.fictionStatus === 'FICTIONAL_WORLD') return 'Fictional world layer. Buyer-sensitive facts must come from verified sources.';
  return 'Prototype content. Verify source status before client-facing use.';
}

function createHotspotLabel() {
  const label = document.createElement('div');
  label.className = 'hotspot-label';
  document.body.appendChild(label);
  return label;
}

function euro(value) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
}
function euroShort(value) { return `€${Math.round(value / 1000)}k`; }

function onResize() {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
}

// QA introspection surface (used by qa/perf.mjs and qa/capture.mjs).
window.__qa = {
  authority, state, store, renderer, scene, walker, setMode, openEntity, closeDetail,
  frameTimes: () => frameTimesRing.slice(),
  resetFrameTimes: () => { frameTimesRing.length = 0; },
  worldPositionOf(entityId) {
    let found = null;
    scene.traverse((o) => { if (!found && o.userData?.entityId === entityId) found = o; });
    if (!found) return null;
    const p = new THREE.Vector3();
    found.getWorldPosition(p);
    return [p.x, p.y, p.z];
  },
  // Debug probe: raycast the full scene through NDC coords, return hit chain.
  pickAt(nx, ny) {
    raycaster.setFromCamera({ x: nx, y: ny }, camera);
    return raycaster.intersectObjects(scene.children, true).slice(0, 3).map((hit) => {
      const chain = [];
      let node = hit.object;
      while (node && node !== scene) { chain.unshift(node.geometry?.type || node.type); node = node.parent; }
      const p = hit.point;
      return { geo: hit.object.geometry?.type, point: [p.x.toFixed(2), p.y.toFixed(2), p.z.toFixed(2)], chain: chain.join('>') };
    });
  }
};
